import { useState, type JSX } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/api.js';
import { useI18n } from '../lib/i18n.js';
import { Card, Empty, ErrorBox, Filter, Ltr, Row, Spinner, StatusChip } from '../components/ui.js';
import type { IssueSummary } from '../lib/types.js';

interface SearchResponse {
  jql: string;
  issues: IssueSummary[];
  nextPageToken: string | null;
  isLast: boolean;
}

const QUICK: Array<{ key: string; labelKey: string }> = [
  { key: 'assignedToMe', labelKey: 'home.assigned' },
  { key: 'reportedByMe', labelKey: 'home.reported' },
  { key: 'watchedByMe', labelKey: 'home.watched' },
  { key: 'myOverdue', labelKey: 'home.overdue' },
  { key: 'myDueThisWeek', labelKey: 'home.due_soon' },
  { key: 'myActiveSprint', labelKey: 'home.sprint' },
  { key: 'recentlyUpdated', labelKey: 'home.recent' },
];

export function IssuesPage(): JSX.Element {
  const { t } = useI18n();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();

  const quick = params.get('quick') ?? 'assignedToMe';
  const [text, setText] = useState('');
  const [jql, setJql] = useState(params.get('jql') ?? '');
  const [pages, setPages] = useState<string[]>([]);

  const token = pages.length > 0 ? pages[pages.length - 1]! : null;

  const { data, isLoading, error, refetch, isFetching } = useQuery({
    queryKey: ['search', quick, text, jql, token],
    queryFn: () =>
      api<SearchResponse>('/search', {
        method: 'POST',
        body: {
          ...(jql ? { jql } : text ? { text } : { quick }),
          maxResults: 25,
          nextPageToken: token,
        },
      }),
  });

  return (
    <div className="page">
      <div className="header">
        <h1>{t('common.search')}</h1>
        <button type="button" className="btn btn-secondary" onClick={() => navigate('/create')}>
          ＋
        </button>
      </div>

      <div className="card">
        <div className="field">
          <label htmlFor="text">{t('search.box_label')}</label>
          <input
            id="text"
            value={text}
            placeholder="ABC-123"
            onChange={(event) => {
              setText(event.target.value);
              setJql('');
              setPages([]);
            }}
          />
          <span className="small muted">{t('search.box_hint')}</span>
        </div>
        <details open={jql.length > 0}>
          <summary className="small muted">{t('search.advanced')} · JQL</summary>
          <p className="small muted" style={{ margin: '8px 0 0' }}>
            {t('search.advanced_hint')}
          </p>
          <div className="field" style={{ marginTop: 8 }}>
            <textarea
              className="mono"
              value={jql}
              placeholder="project = ABC AND assignee = currentUser() ORDER BY updated DESC"
              onChange={(event) => {
                setJql(event.target.value);
                setPages([]);
              }}
            />
          </div>
          {data?.jql && data.jql !== jql ? (
            <>
              <div className="small muted">{t('search.showing_for')}</div>
              <div className="small mono muted" style={{ overflowWrap: 'anywhere' }}>
                {data.jql}
              </div>
              <button
                type="button"
                className="btn btn-secondary small"
                onClick={() => {
                  setJql(data.jql);
                  setText('');
                  setPages([]);
                }}
              >
                {t('search.edit_query')}
              </button>
            </>
          ) : null}
        </details>
        <button type="button" className="btn btn-block" onClick={() => void refetch()}>
          {t('common.search')}
        </button>
      </div>

      <div className="filter-row">
        {QUICK.map((item) => (
          <Filter
            key={item.key}
            label={t(item.labelKey)}
            active={quick === item.key && !text && !jql}
            onClick={() => {
              setText('');
              setJql('');
              setPages([]);
              setParams({ quick: item.key });
            }}
          />
        ))}
      </div>

      {error ? <ErrorBox error={error} /> : null}
      {isLoading ? <Spinner /> : null}

      {data ? (
        <Card>
          {data.issues.length === 0 ? (
            <Empty text={t('search.no_results')} />
          ) : (
            data.issues.map((issue) => (
              <Row
                key={issue.key}
                onClick={() => navigate(`/issues/${issue.key}`)}
                title={
                  <>
                    <Ltr>{issue.key}</Ltr> · {issue.summary}
                  </>
                }
                subtitle={
                  <>
                    {issue.assignee?.displayName ?? t('common.unassigned')}
                    {issue.priorityName ? ` · ${issue.priorityName}` : ''}
                  </>
                }
                right={<StatusChip name={issue.statusName} category={issue.statusCategoryKey} />}
              />
            ))
          )}
        </Card>
      ) : null}

      <div className="btn-row">
        {pages.length > 0 ? (
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => setPages((prev) => prev.slice(0, -1))}
          >
            ◀ {t('common.previous')}
          </button>
        ) : null}
        {data?.nextPageToken ? (
          <button
            type="button"
            className="btn"
            disabled={isFetching}
            onClick={() => setPages((prev) => [...prev, data.nextPageToken!])}
          >
            {t('common.next')} ▶
          </button>
        ) : null}
      </div>
    </div>
  );
}
